import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

const AdminDashboard = ({ admin, onLogout }) => {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        amount: '',
        description: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const fetchEnquiries = async () => {
    try {
        setLoading(true);
        const response = await fetch('http://localhost:5000/api/admin/enquiries');
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setEnquiries(data);
    } catch (err) {
        console.error('Error fetching enquiries:', err);
        alert('Error fetching enquiries: ' + err.message);
    } finally {
        setLoading(false);
    }
};

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);

        try {
            const response = await fetch('http://localhost:5000/api/enquiries', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                alert('Enquiry added successfully! Payment link has been sent to the email.');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    amount: '',
                    description: ''
                });
                fetchEnquiries();
                console.log('Payment Link:', data.paymentLink);
            } else {
                alert(data.message || 'Error adding enquiry');
            }
        } catch (err) {
            alert('Network error. Please try again.');
        } finally {
            setFormLoading(false);
        }
    };

    const handleLogout = () => {
        onLogout();
        navigate('/');
    };

    const getPaymentStatus = (enquiry) => {
        return enquiry.payment_active === 1 ? 'Paid' : 'Pending';
    };

    const getStatusClass = (enquiry) => {
        return enquiry.payment_active === 1 ? 'paid' : 'pending';
    };

    return (
        <div className="admin-dashboard-container">
            <div className="admin-dashboard-header">
                <h1>Admin Dashboard - Ascend Travel Solutions</h1>
                <div className="admin-info">
                    <span>Welcome, {admin?.admin_name || 'Admin'}</span>
                    <button onClick={handleLogout} className="logout-btn">
                        Logout
                    </button>
                </div>
            </div>

            {/* Add Enquiry Form */}
            <div className="enquiry-form-section">
                <h2>Add New Enquiry</h2>
                <form onSubmit={handleSubmit} className="enquiry-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="name">Name *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter customer name"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter customer email"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="phone">Phone *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter customer phone"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="amount">Amount</label>
                            <input
                                type="number"
                                id="amount"
                                name="amount"
                                value={formData.amount}
                                onChange={handleInputChange}
                                placeholder="Enter amount"
                                min="0"
                                step="0.01"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter enquiry description"
                            rows="4"
                        />
                    </div>

                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={formLoading}
                    >
                        {formLoading ? 'Adding Enquiry...' : 'Add Enquiry'}
                    </button>
                </form>
            </div>

            {/* Enquiries Table */}
            <div className="enquiries-table-section">
                <div className="table-header">
                    <h2>Enquiries List ({enquiries.length})</h2>
                    <button
                        onClick={fetchEnquiries}
                        className="refresh-btn"
                        disabled={loading}
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>

                {loading ? (
                    <div className="loading">Loading enquiries...</div>
                ) : enquiries.length === 0 ? (
                    <p className="no-data">No enquiries found.</p>
                ) : (
                    <div className="table-container">
                        <table className="enquiries-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Amount</th>
                                    <th>Description</th>
                                    <th>Reference No</th>
                                    <th>Payment Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {enquiries.map((enquiry) => (
                                    <tr key={enquiry.enquiries_pkid}>
                                        <td>{enquiry.enquiries_pkid}</td>
                                        <td>{enquiry.name}</td>
                                        <td>{enquiry.email}</td>
                                        <td>{enquiry.phone}</td>
                                        <td>₹{enquiry.amount || '0'}</td>
                                        <td className="description-cell">
                                            {enquiry.description || '-'}
                                        </td>
                                        <td>{enquiry.ref_no || '-'}</td>
                                        <td>
                                            <span className={`status-badge status-${getStatusClass(enquiry)}`}>
                                                {getPaymentStatus(enquiry)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;